import { useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ApiGatewayError, usersProxy } from "./apiGateway";

type UserAvatarProps = {
  user: {
    id: string;
    avatarId?: string | null;
    avatarUrl?: string | null;
    username?: string | null;
    nickname: string;
  };
  role?: string;
  onRefresh: () => Promise<unknown>;
  onDeleted: () => void;
};

type DeletionTarget = { userId: string; avatarId: string; avatarUrl: string };
type DeletionError = { message: string; retryable: boolean };

const DELETE_AVATAR_ROLES = new Set([
  "manager-admin",
  "moderator-admin",
  "moderator",
]);

const getDeletionError = (error: unknown): DeletionError => {
  if (error instanceof ApiGatewayError) {
    switch (error.errorId) {
      case "USER_NOT_FOUND":
        return { message: "Пользователь не найден.", retryable: false };
      case "AVATAR_NOT_FOUND":
        return {
          message:
            "Фото не найдено. Возможно, оно уже удалено. Обновите профиль.",
          retryable: false,
        };
      case "BAD_REQUEST":
        return {
          message: "Некорректные данные для удаления фото. Обновите профиль.",
          retryable: false,
        };
    }
    if (error.status === 401 || error.status === 403) {
      return { message: "Нет доступа к удалению фото.", retryable: false };
    }
    if (error.status === 400) {
      return {
        message: "Некорректные данные для удаления фото. Обновите профиль.",
        retryable: false,
      };
    }
    if (error.status === 404) {
      return {
        message: "Пользователь или фото не найдены. Обновите профиль.",
        retryable: false,
      };
    }
  }
  return {
    message:
      "Не удалось удалить фото. Повторите запрос: повтор безопасен для выбранного фото.",
    retryable: true,
  };
};

export const UserAvatar = ({
  user,
  role,
  onRefresh,
  onDeleted,
}: UserAvatarProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState<DeletionTarget | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState<DeletionError | null>(null);
  const inFlight = useRef(false);
  const deletionSucceeded = useRef(false);
  const confirmation = useRef<string | undefined>(undefined);
  const canDelete = DELETE_AVATAR_ROLES.has(role ?? "");

  const openConfirmation = () => {
    if (!canDelete || inFlight.current) return;
    if (!deletionSucceeded.current && !confirmation.current) {
      if (!user.avatarUrl || !user.avatarId) return;
      setTarget({
        userId: user.id,
        avatarId: user.avatarId,
        avatarUrl: user.avatarUrl,
      });
      setError(null);
    }
    setDialogOpen(true);
  };

  const closeConfirmation = () => {
    if (inFlight.current) return;
    setDialogOpen(false);
    if (!deletionSucceeded.current && !confirmation.current) {
      setTarget(null);
      setError(null);
    }
  };

  const submit = async () => {
    if (
      !canDelete ||
      !target ||
      target.userId !== user.id ||
      inFlight.current ||
      (error && !error.retryable)
    )
      return;
    // The ref also prevents two submissions before React renders the disabled button.
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (!deletionSucceeded.current) {
        await usersProxy.deleteAvatar({
          userId: target.userId,
          avatarId: target.avatarId,
          confirmation: confirmation.current,
        });
        deletionSucceeded.current = true;
        confirmation.current = undefined;
        setDeleted(true);
        onDeleted();
      }
      await onRefresh();
      setPreviewOpen(false);
      setDialogOpen(false);
      setTarget(null);
      deletionSucceeded.current = false;
      setDeleted(false);
    } catch (requestError) {
      if (requestError instanceof ApiGatewayError) {
        const payload = requestError.payload as {
          body?: { avatarDeletionConfirmation?: unknown };
        } | null;
        const receipt = payload?.body?.avatarDeletionConfirmation;
        if (typeof receipt === "string") confirmation.current = receipt;
      }
      const deletionError = getDeletionError(requestError);
      setError(
        deletionSucceeded.current
          ? {
              message:
                "Фото удалено. Не удалось обновить профиль. Повторите загрузку профиля.",
              retryable: true,
            }
          : confirmation.current && deletionError.retryable
            ? {
                message:
                  "Фото удалено. Не удалось сохранить историю. Повторите запрос, чтобы завершить запись.",
                retryable: true,
              }
            : deletionError,
      );
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        flexShrink: 0,
      }}
    >
      <ButtonBase
        disabled={!user.avatarUrl}
        onClick={() => setPreviewOpen(true)}
        aria-label="Открыть фото профиля"
        sx={{ borderRadius: "50%" }}
      >
        <Avatar
          alt={user.username || user.nickname || "Аватар пользователя"}
          src={user.avatarUrl || undefined}
          sx={{ width: 120, height: 120 }}
        />
      </ButtonBase>
      {canDelete && (user.avatarUrl || deleted) && (
        <Box>
          <Button
            color="error"
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={openConfirmation}
            disabled={submitting || (!deleted && !user.avatarId)}
          >
            {deleted ? "Обновить профиль" : "Удалить фото"}
          </Button>
          {!deleted && !user.avatarId && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              ID фото отсутствует. Обновите профиль.
            </Typography>
          )}
        </Box>
      )}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Аватар пользователя</DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            component="img"
            src={user.avatarUrl || ""}
            alt="Фото профиля"
            sx={{ maxWidth: "100%", borderRadius: 2 }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={dialogOpen}
        onClose={closeConfirmation}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-user-avatar-title"
      >
        <DialogTitle id="delete-user-avatar-title">
          {deleted ? "Обновить профиль" : "Удалить фото профиля?"}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2 }}>
          {!deleted && target && (
            <>
              <Box
                component="img"
                src={target.avatarUrl}
                alt="Удаляемое фото профиля"
                sx={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  justifySelf: "center",
                  borderRadius: 2,
                }}
              />
            </>
          )}
          {error && <Alert severity="error">{error.message}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmation} disabled={submitting}>
            {deleted ? "Закрыть" : "Отмена"}
          </Button>
          {(!error || error.retryable) && (
            <Button
              variant="contained"
              color={deleted ? "primary" : "error"}
              onClick={submit}
              disabled={submitting}
            >
              {submitting
                ? deleted
                  ? "Загрузка..."
                  : "Удаление..."
                : deleted
                  ? "Обновить профиль"
                  : error
                    ? "Повторить"
                    : "Удалить"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
